// Şub 27, 2026 — DPD Şoför Performansı Raporu (Phase B)
// Tarih aralığında şoför/tur bazlı agregasyon: paket, retour, teslim oranı, km, süre
import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { useTranslation } from '../../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import {
  UserRound, RefreshCw, Loader2, TrendingUp, Package, Truck, ArrowUpDown,
  Clock, AlertCircle, Sunrise,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { toast } from 'sonner';
import DPDMonthlyWorkhoursChart from './DPDMonthlyWorkhoursChart';
import DPDPerfKpiDialog from './DPDPerfKpiDialog';

const rateColor = (rate) => {
  if (rate >= 95) return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
  if (rate >= 90) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
  if (rate >= 85) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
  if (rate >= 80) return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
  return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

export default function DPDDriverPerformance() {
  const { t } = useTranslation();
  const [firms, setFirms] = useState([]);
  const [firmId, setFirmId] = useState('');
  const [dateFrom, setDateFrom] = useState(daysAgoIso(7));
  const [dateTo, setDateTo] = useState(todayIso());
  const [groupBy, setGroupBy] = useState('courier'); // courier | tour
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('total_parcels');
  const [sortDir, setSortDir] = useState('desc');
  const [q, setQ] = useState('');
  const [detailKind, setDetailKind] = useState(null); // clicked KPI kart

  useEffect(() => {
    api.get('/admin/firms').then((r) => {
      const list = r.data?.data || [];
      setFirms(list);
      const ft = list.find((f) => /fast.?trans/i.test(f.name)) || list[0];
      if (ft) setFirmId(ft.id);
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    if (!firmId || !dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const r = await api.get(
        `/dpd/performance?firm_id=${firmId}&date_from=${dateFrom}&date_to=${dateTo}&group_by=${groupBy}`
      );
      setData(r.data?.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [firmId, dateFrom, dateTo, groupBy]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchTrend = useCallback(async () => {
    if (!firmId) return;
    try {
      const r = await api.get(`/dpd/performance-trend?firm_id=${firmId}&weeks=8&group_by=${groupBy}`);
      setTrend(r.data?.data);
    } catch (e) { /* ignore */ }
  }, [firmId, groupBy]);
  useEffect(() => { fetchTrend(); }, [fetchTrend]);

  const toggleSort = (k) => {
    if (sortKey === k) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(k);
      setSortDir('desc');
    }
  };

  const rows = (data?.rows || [])
    .filter((r) => !q || r.key?.toLowerCase().includes(q.toLowerCase()))
    .slice()
    .sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });

  const totals = data?.totals || {};

  const setPreset = (days) => {
    setDateFrom(daysAgoIso(days));
    setDateTo(todayIso());
  };

  const SortHeader = ({ k, children, align = 'left' }) => (
    <TableHead className={`cursor-pointer select-none ${align === 'right' ? 'text-right' : ''}`} onClick={() => toggleSort(k)}>
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowUpDown className={`h-3 w-3 ${sortKey === k ? 'opacity-100' : 'opacity-30'}`} />
      </span>
    </TableHead>
  );

  return (
    <div className="space-y-4" data-testid="dpd-driver-performance-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserRound className="h-5 w-5 text-blue-600" />{t('dpd.driverPerformance.title')}
          </CardTitle>
          <CardDescription>
            {t('dpd.driverPerformance.subtitle', { group: groupBy === 'courier' ? t('dpd.common.courier').toLowerCase() : t('dpd.common.tour').toLowerCase() })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-4">
            <Select value={firmId} onValueChange={setFirmId}>
              <SelectTrigger data-testid="perf-firm-select"><SelectValue placeholder={t('dpd.common.firm')} /></SelectTrigger>
              <SelectContent>
                {firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="perf-date-from" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="perf-date-to" />
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger data-testid="perf-group-by"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="courier">{t('dpd.driverPerformance.groupCourier')}</SelectItem>
                <SelectItem value="tour">{t('dpd.driverPerformance.groupTour')}</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder={t('dpd.common.search')} value={q} onChange={(e) => setQ(e.target.value)} data-testid="perf-search" />
            <Button onClick={fetchData} disabled={loading} variant="outline" data-testid="perf-refresh-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              {t('dpd.common.refresh')}
            </Button>
          </div>

          <div className="flex gap-2 mb-4 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => setPreset(7)} data-testid="perf-preset-7d">{t('dpd.common.preset7')}</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset(14)} data-testid="perf-preset-14d">{t('dpd.common.preset14')}</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset(30)} data-testid="perf-preset-30d">{t('dpd.common.preset30')}</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset(90)} data-testid="perf-preset-90d">{t('dpd.common.preset90')}</Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
            <StatCard icon={UserRound} label={groupBy === 'courier' ? t('dpd.common.courier') : t('dpd.common.tour')} value={totals.unique_keys || 0} color="blue" onClick={() => setDetailKind('drivers')} />
            <StatCard icon={Package} label={t('dpd.driverPerformance.cols.parcels')} value={totals.total_parcels || 0} color="green" onClick={() => setDetailKind('parcels')} />
            <StatCard icon={Truck} label={t('dpd.common.km')} value={totals.total_km || 0} color="purple" onClick={() => setDetailKind('km')} />
            <StatCard icon={Package} label={t('dpd.common.retour')} value={totals.total_retour || 0} color="red" onClick={() => setDetailKind('retour')} />
            <StatCard icon={TrendingUp} label={t('dpd.common.deliveryRate')} value={`%${totals.overall_delivery_rate || 0}`} color="orange" onClick={() => setDetailKind('rate')} />
            {/* Şub 27, 2026 — Ortalama çalışma saati metrikleri */}
            <StatCard icon={Sunrise} label="Ort. Başlangıç" value={totals.overall_avg_start_time || '-'} color="amber" onClick={() => setDetailKind('start')} />
            <StatCard icon={Clock} label="Ort. Saat/Gün" value={totals.overall_avg_working_hours ? `${totals.overall_avg_working_hours}h` : '-'} color="indigo" onClick={() => setDetailKind('hours')} />
          </div>

          {/* Weekly trend chart */}
          {trend?.series?.length > 0 && (
            <div className="mb-4 border rounded-lg p-3" data-testid="perf-trend-chart">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold flex items-center gap-1">
                  <TrendingUp className="h-4 w-4 text-indigo-600" />
                  {t('dpd.driverPerformance.trendTitle')}
                </h3>
                <span className="text-[11px] text-gray-500">{trend.date_from} → {trend.date_to}</span>
              </div>
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                  <LineChart data={trend.series} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} domain={[70, 100]} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line yAxisId="left" type="monotone" dataKey="parcels" name="Paket" stroke="#4f46e5" strokeWidth={2} dot={{ r: 3 }} />
                    <Line yAxisId="left" type="monotone" dataKey="retour" name="Retour" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
                    <Line yAxisId="right" type="monotone" dataKey="delivery_rate" name="Başarı %" stroke="#10b981" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Şub 28, 2026 — Aylık/Yıllık ort. çalışma saati & tur adedi */}
          <div className="mb-4">
            <DPDMonthlyWorkhoursChart firmId={firmId} />
          </div>

          <div className="rounded-lg border overflow-x-auto">
            <Table className="text-sm">
              <TableHeader>
                <TableRow>
                  <SortHeader k="key">{groupBy === 'courier' ? 'Kurye' : 'Tur'}</SortHeader>
                  <SortHeader k="days" align="right">Gün</SortHeader>
                  <TableHead>{groupBy === 'courier' ? 'Turlar' : 'Kuryeler'}</TableHead>
                  <SortHeader k="total_parcels" align="right">Paket</SortHeader>
                  <SortHeader k="total_stops" align="right">Stops</SortHeader>
                  <SortHeader k="retour_parcels" align="right">Retour</SortHeader>
                  <SortHeader k="delivery_rate" align="right">Teslim %</SortHeader>
                  <SortHeader k="total_km" align="right">Km</SortHeader>
                  <SortHeader k="total_hours" align="right">Saat</SortHeader>
                  {/* Şub 27, 2026 — Çalışma saati sütunları */}
                  <SortHeader k="avg_start_time" align="right">Ort. Başlangıç</SortHeader>
                  <SortHeader k="avg_end_time" align="right">Ort. Bitiş</SortHeader>
                  <SortHeader k="avg_working_hours_per_day" align="right">Ort. Saat/Gün</SortHeader>
                  <SortHeader k="parcels_per_hour" align="right">Paket/Saat</SortHeader>
                  <SortHeader k="avg_km_per_day" align="right">Ort. Km/Gün</SortHeader>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {Array.from({ length: 14 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))}
                {!loading && rows.length === 0 && (
                  <TableRow><TableCell colSpan={14} className="text-center text-gray-500 py-8">
                    {t('dpd.driverPerformance.empty')}
                  </TableCell></TableRow>
                )}
                {!loading && rows.map((r) => (
                  <TableRow key={r.key} data-testid={`perf-row-${r.key}`}>
                    <TableCell className="font-medium">
                      {groupBy === 'tour'
                        ? <Badge variant="outline" className="font-mono">{r.key}</Badge>
                        : <span className="truncate max-w-[220px] block" title={r.key}>{r.key}</span>}
                    </TableCell>
                    <TableCell className="text-right">{r.days}</TableCell>
                    <TableCell className="max-w-[180px]">
                      <div className="flex flex-wrap gap-1">
                        {(groupBy === 'courier' ? r.tours : r.couriers).slice(0, 4).map((x) => (
                          <Badge key={x} variant="secondary" className="text-[10px] font-mono">{x}</Badge>
                        ))}
                        {(groupBy === 'courier' ? r.tours : r.couriers).length > 4 && (
                          <Badge variant="secondary" className="text-[10px]">+{(groupBy === 'courier' ? r.tours : r.couriers).length - 4}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold">{r.total_parcels}</TableCell>
                    <TableCell className="text-right">{r.total_stops}</TableCell>
                    <TableCell className="text-right">
                      <span className={`px-2 py-0.5 rounded ${r.retour_parcels > 20 ? 'bg-red-50 text-red-700' : 'text-gray-700'}`}>
                        {r.retour_parcels}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`px-2 py-0.5 rounded font-medium ${rateColor(r.delivery_rate)}`}>
                        %{r.delivery_rate}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">{r.total_km}</TableCell>
                    <TableCell className="text-right">{r.total_hours}</TableCell>
                    {/* Şub 27, 2026 — Çalışma saati verileri */}
                    <TableCell className="text-right font-mono text-xs">
                      {r.avg_start_time ? (
                        <span className={r.flag_late_start ? 'px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' : 'text-gray-700 dark:text-gray-300'}>
                          {r.avg_start_time}
                          {r.flag_late_start && <AlertCircle className="inline h-3 w-3 ml-0.5" title="Geç başlangıç (09:00+)" />}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs text-gray-700 dark:text-gray-300">
                      {r.avg_end_time || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.avg_working_hours_per_day ? (
                        <span className={r.flag_overtime ? 'px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 font-semibold' : ''}>
                          {r.avg_working_hours_per_day}h
                          {r.flag_overtime && <AlertCircle className="inline h-3 w-3 ml-0.5" title="Fazla mesai (9h+)" />}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-right text-gray-700 dark:text-gray-300">{r.parcels_per_hour || 0}</TableCell>
                    <TableCell className="text-right text-gray-600">{r.avg_km_per_day}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {rows.length > 0 && (
            <p className="text-xs text-gray-500 mt-3">
              {t('dpd.driverPerformance.colorLegend')}{' '}
              <span className="inline-block px-1.5 rounded bg-emerald-100 text-emerald-800">%95+</span>{' '}
              <span className="inline-block px-1.5 rounded bg-green-100 text-green-800">%90+</span>{' '}
              <span className="inline-block px-1.5 rounded bg-yellow-100 text-yellow-800">%85+</span>{' '}
              <span className="inline-block px-1.5 rounded bg-orange-100 text-orange-800">%80+</span>{' '}
              <span className="inline-block px-1.5 rounded bg-red-100 text-red-800">%80-</span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* KPI Kart Detay Dialog — Şub 28, 2026 (Her karta özel içerik) */}
      <DPDPerfKpiDialog
        open={!!detailKind}
        onClose={() => setDetailKind(null)}
        kind={detailKind}
        firmId={firmId}
        totals={totals}
        groupBy={groupBy}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, onClick }) {
  const colors = {
    orange: 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300',
    blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
    green: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300',
    purple: 'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300',
    red: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
    indigo: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300',
  };
  const clickable = typeof onClick === 'function';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={`text-left rounded-lg p-3 transition ${colors[color]} ${clickable ? 'cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-current/40' : ''}`}
      data-testid={`perf-stat-${label.toLowerCase().replace(/\s+/g,'-')}`}
    >
      <div className="flex items-center gap-2 text-xs opacity-80"><Icon className="h-4 w-4" />{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </button>
  );
}
